import { hasProjectPages } from "../pages/project-pages";
import { CornerRadiusEnum } from "./corner-radius";

/**
 * Structural subset of a division and its brandkits. Kept minimal so both the
 * api-private and api-public `DivisionEntity` shapes (server render vs. client
 * config blob) satisfy it, the same way `base-brandkit-theme.ts` does.
 */
interface BrandkitLike {
  id: string;
  /** Unset (or `default`) means the brandkit inherits the page's radius. */
  cornerRadius?: CornerRadiusEnum | string;
}

interface ThemeableDivisionLike<TBrandkit extends BrandkitLike> {
  workspaceEnabled?: boolean;
  pagesEnabled?: boolean;
  brandkits?: TBrandkit[] | null;
  company?: { brandkits?: TBrandkit[] | null } | null;
}

export interface ThemeableBrandkit<TBrandkit extends BrandkitLike> {
  brandkit: TBrandkit;
  /** From the organization rather than this project — see `mergeBrandkitOverrides`. */
  inherited: boolean;
}

/**
 * The brandkits a division's pages can reference: its own, plus — for
 * projects with pages — the organization's, since distributed master pages
 * carry org brandkit ids (page-level and per-block `activeBrandkitId`) and a
 * project's own pages fall back to the organization's default.
 * Deduped by id with division-owned kits winning.
 *
 * Shared because both sides of the same variable blocks are built from it: SSR
 * emits a `[data-theme="<id>"]` rule per entry, and the pages runtime overrides
 * the accent in those same rules. A list that disagreed would emit a rule SSR
 * never shipped — or miss one it did.
 */
export function themeableBrandkits<TBrandkit extends BrandkitLike>(
  division: ThemeableDivisionLike<TBrandkit> | undefined
): ThemeableBrandkit<TBrandkit>[] {
  const own = (division?.brandkits || []).map((brandkit) => ({
    brandkit,
    inherited: false,
  }));
  if (!division?.workspaceEnabled && !hasProjectPages(division)) return own;

  const seen = new Set(own.map(({ brandkit }) => brandkit.id));
  const org = (division?.company?.brandkits || [])
    .filter((brandkit) => !seen.has(brandkit.id))
    .map((brandkit) => ({ brandkit, inherited: true }));

  return [...own, ...org];
}
